import axios from 'axios';

// Base URL for the API
const API_BASE_URL = (process.env.VUE_APP_API_URL || 'http://localhost:5000') + '/api';

/**
 * Service for analytics data retrieval
 */
class AnalyticsService {
    
    /**
     * Get route history data for analytics
     * 
     * @returns {Promise} - Promise with the route data
     */
    async getRouteData() {
        return axios.get(`${API_BASE_URL}/analytics/route-data`);
    }
    
    /**
     * Get heatmap data for analytics
     * 
     * @returns {Promise} - Promise with the heatmap data
     */
    async getHeatmapData() {
        return axios.get(`${API_BASE_URL}/analytics/heatmap-data`);
    }

    /**
     * Save route data for analytics
     * 
     * @param {Object} routeData - The route data to be saved
     * @returns {Promise} - Promise indicating the result of the save operation
     */
    async saveRoute(routeData) {
        return axios.post(`${API_BASE_URL}/analytics/save-route`, routeData);
    }

    /**
     * Get saved routes for the data catalog
     * 
     * @returns {Promise} - Promise with the saved routes data
     */
    async getSavedRoutes() {
        return axios.get(`${API_BASE_URL}/analytics/saved-routes`)
            .catch(error => {
                console.warn('Error fetching saved routes:', error.response?.data || error.message);
                // Show the actual error message returned by the server
                if (error.response && error.response.data) {
                    console.log('Server error details:', error.response.data);
                    // If we have a test document from server, return it
                    if (error.response.data.test_document) {
                        return { data: [error.response.data.test_document] };
                    }
                }
                // Fallback to route-data if saved-routes endpoint doesn't exist yet
                return this.getRouteData();
            });
    }

    /**
     * Get locations for the data catalog
     * 
     * @returns {Promise} - Promise with the locations data
     */
    async getLocations() {
        return axios.get(`${API_BASE_URL}/locations`)
            .catch(error => {
                console.warn('Error fetching locations:', error);
                // Return empty array if endpoint doesn't exist yet
                return { data: [] };
            });
    }    /**
     * Get order points for the data catalog
     * 
     * @returns {Promise} - Promise with the order points data
     */
    async getOrderPoints() {
        return axios.get(`${API_BASE_URL}/orders`)
            .catch(error => {
                console.warn('Error fetching order points:', error);
                // Return empty array if endpoint doesn't exist yet
                return { data: [] };
            });
    }

    /**
     * Get available columns for orders data analysis
     * 
     * @returns {Promise} - Promise with the columns data
     */
    async getOrderColumns() {
        return axios.get(`${API_BASE_URL}/orders/columns`)
            .catch(error => {
                console.warn('Error fetching order columns:', error);
                return { data: { columns: [], aggregations: [] } };
            });
    }

    /**
     * Aggregate orders data by H3 hexagons
     * 
     * @param {Object} params - Aggregation parameters
     * @returns {Promise} - Promise with the aggregated data
     */
    async aggregateOrders(params) {
        return axios.post(`${API_BASE_URL}/orders/aggregate`, params)
            .catch(error => {
                console.warn('Error aggregating orders:', error);
                return { data: { data: [], column: '', aggregation: '', total_hexagons: 0 } };
            });
    }

    /**
     * Get analytics locations (malls, retail stores) for the data catalog
     * 
     * @param {Object} params - Query parameters (category, limit, skip)
     * @returns {Promise} - Promise with the analytics locations data
     */
    async getAnalyticsLocations(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.category) queryParams.append('category', params.category);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.skip) queryParams.append('skip', params.skip);

        const queryString = queryParams.toString();
        const url = `${API_BASE_URL}/analytics/locations${queryString ? '?' + queryString : ''}`;
        
        return axios.get(url)
            .catch(error => {
                console.warn('Error fetching analytics locations:', error);
                return { data: { data: [], categories: [], total_count: 0 } };
            });
    }

    /**
     * Get analytics location categories for grouping
     * 
     * @returns {Promise} - Promise with the categories data
     */
    async getAnalyticsLocationCategories() {
        return axios.get(`${API_BASE_URL}/analytics/locations/categories`)
            .catch(error => {
                console.warn('Error fetching analytics location categories:', error);
                return { data: { categories: [] } };
            });
    }

    /**
     * Get analytics locations in GeoJSON format for map visualization
     * 
     * @param {Object} params - Query parameters (category, bbox, limit)
     * @returns {Promise} - Promise with the GeoJSON data
     */
    async getAnalyticsLocationsGeoJSON(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.category) queryParams.append('category', params.category);
        if (params.bbox) queryParams.append('bbox', params.bbox);
        if (params.limit) queryParams.append('limit', params.limit);

        const queryString = queryParams.toString();
        const url = `${API_BASE_URL}/analytics/locations/geojson${queryString ? '?' + queryString : ''}`;
        
        return axios.get(url)
            .catch(error => {
                console.warn('Error fetching analytics locations GeoJSON:', error);
                return { data: { type: 'FeatureCollection', features: [] } };
            });
    }

    /**
     * Get statistics about analytics locations
     * 
     * @returns {Promise} - Promise with the statistics data
     */
    async getAnalyticsLocationStats() {
        return axios.get(`${API_BASE_URL}/analytics/locations/stats`)
            .catch(error => {
                console.warn('Error fetching analytics location stats:', error);
                return { 
                    data: { 
                        total_count: 0, 
                        category_distribution: [], 
                        type_distribution: [], 
                        top_h3_hexagons: [] 
                    } 
                };
            });
    }
}

export default new AnalyticsService();
